'use client';

import { useState, useRef, useEffect } from 'react';
import toast from 'react-hot-toast';
import { createClient } from '@/lib/supabase/client';
import type { Employee } from './types';

interface PhotoUploadModalProps {
  employee: Employee | null;
  onClose: () => void;
  onUploaded: (newAvatarUrl: string) => void;
}

const PhotoUploadModal = ({ employee, onClose, onUploaded }: PhotoUploadModalProps) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const supabase = createClient();

  useEffect(() => {
    if (!employee) {
      setPreviewUrl(null);
      setSelectedFile(null);
      setZoom(1);
      setOffsetX(0);
      setOffsetY(0);
      setSubmitting(false);
    }
  }, [employee?.id]);

  useEffect(() => {
    if (!employee) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [employee, submitting, onClose]);

  if (!employee) return null;

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be under 10MB');
      return;
    }
    setSelectedFile(file);
    setZoom(1);
    setOffsetX(0);
    setOffsetY(0);
    const reader = new FileReader();
    reader.onload = (ev) => setPreviewUrl(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const cropToCanvas = async (): Promise<Blob | null> => {
    if (!previewUrl || !selectedFile) return null;
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = 512;
        canvas.height = 512;
        const ctx = canvas.getContext('2d');
        if (!ctx) return resolve(null);

        const baseSize = Math.min(img.width, img.height);
        const sourceSize = baseSize / zoom;
        const sourceX = Math.max(0, Math.min(img.width - sourceSize, (img.width - sourceSize) / 2 - offsetX));
        const sourceY = Math.max(0, Math.min(img.height - sourceSize, (img.height - sourceSize) / 2 - offsetY));

        ctx.drawImage(img, sourceX, sourceY, sourceSize, sourceSize, 0, 0, 512, 512);
        canvas.toBlob(blob => resolve(blob), 'image/jpeg', 0.9);
      };
      img.src = previewUrl;
    });
  };

  // Safe JSON parser — throws a readable error if the server returns HTML (e.g. 502)
  const safeJson = async (resp: Response, label: string) => {
    const ct = resp.headers.get('content-type') ?? '';
    if (!ct.includes('application/json')) {
      throw new Error(`${label} returned unexpected response (HTTP ${resp.status}). Please try again.`);
    }
    return resp.json();
  };

  const handleUpload = async () => {
    if (!selectedFile || !previewUrl) return;
    setSubmitting(true);

    try {
      const croppedBlob = await cropToCanvas();
      if (!croppedBlob) throw new Error('Failed to process image');

      const ext = selectedFile.name.split('.').pop()?.toLowerCase() ?? 'jpg';
      const filename = `avatar_${Date.now()}.${ext === 'jpg' || ext === 'jpeg' || ext === 'png' || ext === 'webp' ? ext : 'jpg'}`;

      const sigResp = await fetch('/api/hr/document-upload-url', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          employee_id: employee.id,
          filename,
          content_type: 'image/jpeg',
        }),
      });
      const sigData = await safeJson(sigResp, 'Upload URL endpoint');
      if (!sigResp.ok) throw new Error(sigData.error ?? 'Failed to get upload URL');

      const uploadResp = await fetch(sigData.signed_url, {
        method: 'PUT',
        headers: { 'Content-Type': 'image/jpeg' },
        body: croppedBlob,
      });
      if (!uploadResp.ok) throw new Error('Upload failed');

      const dlResp = await fetch('/api/hr/document-download-url', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ storage_path: sigData.storage_path }),
      });
      const dlData = await safeJson(dlResp, 'Download URL endpoint');

      // Update employees.avatar_url with timeout + retry
      console.log('[PhotoUpload] Storage upload complete. Updating employees.avatar_url:', sigData.storage_path);

      const updateWithRetry = async (): Promise<void> => {
        for (let attempt = 0; attempt < 3; attempt++) {
          const timeoutPromise = new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error(`Avatar update timed out (attempt ${attempt + 1}/3)`)), 8000)
          );
          const updatePromise = supabase
            .from('employees')
            .update({ avatar_url: sigData.storage_path, updated_at: new Date().toISOString() })
            .eq('id', employee.id)
            .then((res: any) => {
              if (res.error) {
                console.error(`[PhotoUpload] Update error attempt ${attempt + 1}:`, res.error);
                throw res.error;
              }
              console.log(`[PhotoUpload] Update succeeded attempt ${attempt + 1}`);
              return res.data;
            });

          try {
            await Promise.race([updatePromise, timeoutPromise]);
            return;
          } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            const isRateLimit = msg.includes('429') || msg.includes('rate') || msg.toLowerCase().includes('too many');
            const isTimeout = msg.includes('timed out');
            console.warn(`[PhotoUpload] Attempt ${attempt + 1} failed: ${msg}`);
            if (attempt === 2 || (!isRateLimit && !isTimeout)) {
              throw err;
            }
            const backoff = 500 * Math.pow(2, attempt);
            console.log(`[PhotoUpload] Backing off ${backoff}ms before retry…`);
            await new Promise(r => setTimeout(r, backoff));
          }
        }
      };

      await updateWithRetry();

      toast.success('Photo updated', { icon: '📸', duration: 3000 });
      onUploaded(dlData.signed_url ?? sigData.storage_path);
      setSubmitting(false);
      onClose();
    } catch (err) {
      console.error('Photo upload failed:', err);
      toast.error(`Upload failed: ${err instanceof Error ? err.message : 'Unknown'}`);
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[120] flex items-center justify-center p-4"
      onClick={submitting ? undefined : onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="photo-upload-title"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between p-5 border-b border-gray-100">
          <div>
            <h3 id="photo-upload-title" className="text-lg font-bold text-[#1C2E5E]">Update Profile Photo</h3>
            <p className="text-[12px] text-gray-500 mt-0.5">{employee.first_name} {employee.last_name}</p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 disabled:opacity-50" aria-label="Close">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        <div className="p-5 space-y-4">
          {!previewUrl ? (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-full p-12 rounded-xl border-2 border-dashed border-gray-300 hover:border-[#E89A3C] hover:bg-blue-50 transition-all flex flex-col items-center gap-2 text-gray-500 hover:text-[#E89A3C]"
            >
              <svg className="w-10 h-10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" strokeLinejoin="round"/>
                <circle cx="12" cy="13" r="4"/>
              </svg>
              <p className="text-[13px] font-semibold">Click to select an image</p>
              <p className="text-[11px] text-gray-400">PNG, JPG, or WEBP up to 10MB</p>
            </button>
          ) : (
            <>
              <div className="relative w-full aspect-square rounded-xl overflow-hidden bg-gray-100">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={previewUrl}
                  alt="Preview"
                  className="w-full h-full object-cover"
                  style={{ transform: `scale(${zoom}) translate(${offsetX}px, ${offsetY}px)` }}
                />
              </div>
              <div className="space-y-2">
                <div>
                  <label className="text-[11px] font-semibold text-gray-700">Zoom</label>
                  <input type="range" min="1" max="3" step="0.05" value={zoom} onChange={e => setZoom(Number(e.target.value))} className="w-full mt-1" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[11px] font-semibold text-gray-700">Pan X</label>
                    <input type="range" min="-100" max="100" value={offsetX} onChange={e => setOffsetX(Number(e.target.value))} className="w-full mt-1" />
                  </div>
                  <div>
                    <label className="text-[11px] font-semibold text-gray-700">Pan Y</label>
                    <input type="range" min="-100" max="100" value={offsetY} onChange={e => setOffsetY(Number(e.target.value))} className="w-full mt-1" />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => { setPreviewUrl(null); setSelectedFile(null); }}
                  className="text-[11px] text-gray-500 hover:text-gray-700 underline"
                >
                  Choose a different image
                </button>
              </div>
            </>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>

        <div className="flex items-center justify-end gap-2 p-5 border-t border-gray-100">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-[13px] font-semibold text-gray-600 hover:bg-gray-100 transition-colors">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleUpload}
            disabled={!selectedFile || submitting}
            className="px-4 py-2 rounded-lg text-[13px] font-semibold text-white bg-[#E89A3C] hover:bg-[#0891c9] disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-2"
          >
            {submitting && (
              <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3"/>
                <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
              </svg>
            )}
            {submitting ? 'Uploading…' : 'Save Photo'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PhotoUploadModal;
